class MultiTabber extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.tabButtons = this.querySelectorAll('.multi-tabber-tab-btn');
    this.panels = this.querySelectorAll('.multi-tabber-panel');
    this.boundListeners = [];

    this.bindEvents();
    this.initDots();
    this.initializeActiveSlider();
  }

  disconnectedCallback() {
    // 1. Remove event listeners attached to tab buttons
    if (this.boundListeners) {
      this.boundListeners.forEach(({ element, type, listener }) => {
        element.removeEventListener(type, listener);
      });
      this.boundListeners = [];
    }

    // 2. Destroy Slick instances inside panels when component is removed
    this.querySelectorAll('[data-home-tab-slider]').forEach((slider) => {
      if (window.jQuery && window.jQuery(slider).hasClass('slick-initialized')) {
        window.jQuery(slider).slick('unslick');
      }
    });

    this.initialized = false;
  }

  initDots() {
    this.dotsContainer = this.querySelector('[data-multi-tabber-dots]');
    if (!this.dotsContainer || !this.tabButtons.length) return;

    this.dotsContainer.innerHTML = '';
    this.dots = [];

    const headingEl = this.querySelector('.multi-tabber-heading');
    const headingText = (headingEl && headingEl.textContent.trim()) || 'Multi Tabber';

    this.tabButtons.forEach((btn, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = `multi-tabber-dot${index === 0 ? ' active' : ''}`;
      dot.setAttribute('aria-label', `Go to tab ${index + 1}`);
      dot.setAttribute('data-datalayer-event', `cta_interaction, ${headingText}`);
      dot.setAttribute('data-click-text', btn.textContent ? btn.textContent.trim() : `Tab ${index + 1}`);

      const clickHandler = () => {
        this.activateTab(index);
      };

      dot.addEventListener('click', clickHandler);
      this.dotsContainer.appendChild(dot);
      this.dots.push(dot);

      if (this.boundListeners) {
        this.boundListeners.push({ element: dot, type: 'click', listener: clickHandler });
      }
    });

    this.updateDots(0);
  }

  updateDots(activeIndex) {
    if (!this.dots || !this.dots.length) return;

    this.dots.forEach((dot, i) => {
      if (i === activeIndex) {
        dot.classList.add('active');
        const activePanel = this.panels[activeIndex];
        if (activePanel) {
          const accent = getComputedStyle(activePanel)
            .getPropertyValue('--theme-accent-color')
            .trim();
          if (accent) {
            dot.style.backgroundColor = accent;
          }
        }
      } else {
        dot.classList.remove('active');
        dot.style.backgroundColor = '';
      }
    });
  }

  bindEvents() {
    this.tabButtons.forEach((btn, index) => {
      const clickHandler = () => {
        this.activateTab(index);
      };

      const keydownHandler = (e) => {
        let nextIndex = null;

        switch (e.key) {
          case 'ArrowRight':
            nextIndex = (index + 1) % this.tabButtons.length;
            break;

          case 'ArrowLeft':
            nextIndex = (index - 1 + this.tabButtons.length) % this.tabButtons.length;
            break;

          case 'Home':
            nextIndex = 0;
            break;

          case 'End':
            nextIndex = this.tabButtons.length - 1;
            break;
        }

        if (nextIndex !== null) {
          e.preventDefault();
          this.tabButtons[nextIndex].focus();
          this.activateTab(nextIndex);
        }
      };

      btn.addEventListener('click', clickHandler);
      btn.addEventListener('keydown', keydownHandler);

      this.boundListeners.push(
        { element: btn, type: 'click', listener: clickHandler },
        { element: btn, type: 'keydown', listener: keydownHandler }
      );
    });
  }

  activateTab(activeIndex) {
    let prevActive = -1;

    this.tabButtons.forEach((btn, i) => {
      if (btn.getAttribute('aria-selected') === 'true') {
        prevActive = i;
      }
    });

    this.tabButtons.forEach((btn, i) => {
      const isSelected = i === activeIndex;

      btn.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      btn.setAttribute('tabindex', isSelected ? '0' : '-1');

      if (!isSelected) return;

      if (prevActive !== -1 && activeIndex > prevActive) {
        (this.tabButtons[i + 1] || btn).scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      } else if (prevActive !== -1 && activeIndex < prevActive) {
        (this.tabButtons[i - 1] || btn).scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      } else {
        btn.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    });

    this.updateDots(activeIndex);

    this.panels.forEach((panel, i) => {
      const isActive = i === activeIndex;

      if (isActive) {
        // Remove hidden first so layout is computed correctly before animation
        panel.removeAttribute('hidden');
        panel.classList.remove('active');
        void panel.offsetWidth; // Force reflow for CSS animation
        panel.classList.add('active');

        const slider = panel.querySelector('[data-home-tab-slider]');

        if (slider) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.initializeSlick(slider);
            });
          });
        }
      } else {
        panel.classList.remove('active');
        panel.setAttribute('hidden', '');
      }
    });
  }

  initializeActiveSlider() {
    this.panels.forEach((panel) => {
      if (!panel.classList.contains('active')) return;

      // Ensure active panel is visible (removes any server-side hidden attr)
      panel.removeAttribute('hidden');

      const slider = panel.querySelector('[data-home-tab-slider]');

      if (slider) {
        this.initializeSlick(slider);
      }
    });
  }

  initializeSlick(slider) {
    if (!slider) return;

    const init = () => {
      if (window._theme && typeof window._theme.initSlickSlider === 'function') {
        // Use jQuery wrapper — FM's initSlickSlider expects a jQuery object
        const $slider = window.jQuery ? window.jQuery(slider) : slider;

        window._theme.initSlickSlider(
          $slider,
          {
            rows: 0,
            dots: false,
            arrows: true,
            accessibility: false,
            infinite: false,
            mobileFirst: true,
            speed: 500,
            slidesToShow: 1.5,
            slidesToScroll: 1,
            responsive: [
              {
                breakpoint: 600,
                settings: {
                  slidesToShow: 2.5,
                  slidesToScroll: 2,
                },
              },
              {
                breakpoint: 991,
                settings: {
                  slidesToShow: 3,
                  slidesToScroll: 3,
                },
              },
              {
                breakpoint: 1300,
                settings: {
                  slidesToShow: 5,
                  slidesToScroll: 5,
                },
              },
              {
                breakpoint: 1500,
                settings: {
                  slidesToShow: 5,
                  slidesToScroll: 5,
                },
              },
            ],
          },
          (slick) => {
            // init callback — remove tabindex from slides (FM standard)
            if (slick && slick.$slider) {
              slick.$slider[0]
                .querySelectorAll('.slick-slide')
                .forEach((slide) => slide.removeAttribute('tabindex'));
            }
          }
        );
      } else {
        setTimeout(init, 50);
      }
    };

    init();
  }
}

if (!customElements.get('multi-tabber')) {
  customElements.define('multi-tabber', MultiTabber);
}
